import React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import {default as Tooltip} from 'components/Tooltip';
import NavLink from 'components/NavLink';
import Feature from 'components/Feature';
import Modal from 'components/Modal';

export default {
  getInitialState() {
    return {
      showEmbedCode: false,
      downloadFocused: false,
      embedCodeCopied: false,
    };
  },

  handleEmbedCodeOpened(event) {
    event.preventDefault();

    this.setState({showEmbedCode: true});
  },

  handleEmbedCodeClosed() {
    this.setState({showEmbedCode: false, embedCodeCopied: false});
  },

  handleDownloadFocused() {
    this.setState({downloadFocused: !this.state.downloadFocused});
  },

  handleDownloadClicked(event) {
    const {
      kitToken,
    } = this.props;
    const {
      kit: kitRequest,
    } = this.requests;

    const rm = this.resourceManager();
    const kit = rm.type(kitRequest, 'kits').find({token: kitToken});

    if (!kit.downloadable) {
      event.preventDefault();
    }
  },

  handleEmbedCodeCopied() {
    this.setState({embedCodeCopied: true});
  },

  renderEmbedCodeModal(kit) {
    const {showEmbedCode, embedCodeCopied, embedCodeUrl, embedCodeScriptTag} = this.state;
    const embedCodeCopiedPopover = (embedCodeCopied) ? <Tooltip noIcon={true} content="Embed Code Copied!" /> : null;
    const cachingNotice = (
      <p className="m-xs-b-md">
        Your embed code is set to live in the browser's cache for <strong>1
        minute</strong>. This means that you will need to wait a full
        minute after publishing to see your changes reflected. If you're
        impatient (like we are) you can force reload your page or clear your
        browser's cache.
      </p>
    );
    const existingCodePromptElement = (
      <div className="alert alert-info bg-body has-icon m-xs-b-0">
        <i className="ficon ficon-info-circle ficon-lg alert-icon"></i>
        {cachingNotice}
        <p className="m-xs-b-md">If you're replacing an existing icon font with Fort Awesome, don't forget to remove it from your website.</p>
        <p>If you're using Fort Awesome alongside an existing icon font <span>(like Font Awesome)</span>, remember to use a different CSS prefix.</p>
      </div>
    );

    let value = null;

    if (embedCodeScriptTag) {
      value = (
        <Modal contentLabel="Embed code" className="modal-dialog modal-lg" isOpen={showEmbedCode} onRequestClose={this.handleEmbedCodeClosed}>
          <div className="modal-content">
            <div className="modal-header bg-body dashed dashed-bottom m-xs-b-xs p-xs-b-sm">
              <button onClick={this.handleEmbedCodeClosed} type="button" className="close close-dangle">
                <span aria-hidden="true"><i className="ficon ficon-times"></i></span><span className="sr-only">Close</span>
              </button>
              <h3 className="modal-title" id="embed-code-modal-label">
                <i className="ficon ficon-code p-xs-r-sm"></i> Embed Code
              </h3>
            </div>
            <div className="modal-body">
              <p>
                After your kit has been published, copy and paste the following embed code into the
                HTML of your website. Make sure it goes near the top of your <code>&lt;head&gt;</code> tag.
              </p>

              <div className="gg m-xs-b-md">
                <div className="gg-col pre text-sm bg-gray-dark inset-darkest font-family-monospace p-xs-x-md p-xs-y-sm br-l">
                  <div className="code code-highlight">
                    <span className="nt">&lt;script</span>&nbsp;
                    <span className="na">src="</span>
                    <span className="s">{embedCodeUrl}</span>
                    <span className="na">"</span>
                    <span className="nt">&gt;</span>
                    <span className="nt">&lt;/script&gt;</span>
                  </div>
                </div>
                <div className="gg-col min-width has-tooltip">
                  {embedCodeCopiedPopover}
                  <CopyToClipboard text={embedCodeScriptTag} onCopy={this.handleEmbedCodeCopied}>
                    <button className="copy-control btn btn-primary p-xs-x-md p-xs-y-sm br-r br-l-0"><i className="ficon ficon-copy"></i></button>
                  </CopyToClipboard>
                </div>
              </div>
              {existingCodePromptElement}
            </div>
            <div className="modal-footer bg-body">
              <button onClick={this.handleEmbedCodeClosed} type="button" className="btn btn-primary-outline">Close <i className="ficon ficon-times-circle"></i></button>
            </div>
          </div>
        </Modal>
      );
    }

    return value;
  },

  renderNavDisabled() {
    const handleClickNoop = (event) => { event.preventDefault(); };

    return (
      <div className="kit-nav nav nav-tabs-inline">
        <ul className="pull-right nav">
          <li className="nav-item disabled"><a className="nav-link" href="#" onClick={handleClickNoop}>Embed Code</a></li>
          <li className="nav-item disabled"><a className="nav-link" href="#" onClick={handleClickNoop}>Download</a></li>
          <li className="nav-item disabled"><a className="nav-link" href="#" onClick={handleClickNoop}>Documentation</a></li>
        </ul>
        <ul className="nav m-xs-r-lg">
          <li className="nav-item disabled"><a className="nav-link" href="#" onClick={handleClickNoop}>Home</a></li>
          <li className="nav-item disabled"><a className="nav-link" href="#" onClick={handleClickNoop}>Icons</a></li>
          <li className="nav-item disabled"><a className="nav-link" href="#" onClick={handleClickNoop}>Typefaces</a></li>
          <Feature name="images">
            <li className="nav-item disabled"><a className="nav-link" href="#" onClick={handleClickNoop}>Images</a></li>
          </Feature>
        </ul>
      </div>
    );
  },

  renderDownloadNavLink(kit, subscription, workingSetArchive) {
    const {downloadFocused} = this.state;
    const isProcessing = workingSetArchive && workingSetArchive.status === 'processing';
    const isFinished = workingSetArchive && workingSetArchive.status === 'finished';
    const downloadClassName = (isFinished) ? 'has-tooltip nav-item' : 'has-tooltip nav-item disabled';
    const handleDownloadClicked = (event) => {
      if (!workingSetArchive) {
        event.preventDefault();
      }
    };

    let popover = null;

    if (downloadFocused) {
      if (!workingSetArchive) {
        popover = <Tooltip noIcon={true} content="Publish your kit<br />to enable download" />;
      }

      if (isProcessing) {
        popover = <Tooltip noIcon={true} content="Creating your download<br /> package now" />;
      }

      if (!subscription.downloadAllowed) {
        popover = <Tooltip noIcon={true} content="Upgrade to enable<br />kit download" />;
      }
    }

    return (
      <li className={downloadClassName}>
        {popover}
        <a className="nav-link"
          href={`/kits/${kit.token}/download`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleDownloadClicked}
          onMouseOver={this.handleDownloadFocused}
          onMouseOut={this.handleDownloadFocused}>Download</a>
      </li>
    );
  },

  renderNavEnabled(kit, subscription, options) {
    return (
      <div className="kit-nav nav nav-tabs-inline">
        {this.renderEmbedCodeModal(kit)}
        <ul className="pull-right nav">
          <li className="nav-item"><a className="nav-link" href="#" onClick={this.handleEmbedCodeOpened}>Embed Code</a></li>
          {this.renderDownloadNavLink(kit, subscription, options.workingSetArchive)}
          <NavLink to={`/kits/${kit.token}/docs`}>Documentation</NavLink>
        </ul>
        <ul className="nav m-xs-r-lg">
          <NavLink to={`/kits/${kit.token}`} activeOnIndex={true}>Home</NavLink>
          <NavLink to={`/kits/${kit.token}/icons`}>Icons</NavLink>
          <NavLink to={`/kits/${kit.token}/typefaces`}>Typefaces</NavLink>
          <Feature name="images">
            <NavLink to={`/kits/${kit.token}/images`}>Images</NavLink>
          </Feature>
        </ul>
      </div>
    );
  },

  renderNav(kit, subscription, options = {}) {
    const optionsWithDefaults = Object.assign({}, {
      disabled: false,
      workingSetArchive: null,
    }, options);

    return (optionsWithDefaults.disabled) ? this.renderNavDisabled() : this.renderNavEnabled(kit, subscription, optionsWithDefaults);
  },
};



// WEBPACK FOOTER //
// ./app/javascript/views/Kits/mixins/MainNavMixin.js